import { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { Icon } from "../../Icons";

interface FloatingToolbarProps {
  editor: Editor | null;
}

/**
 * Floating toolbar that appears when text is selected.
 * Provides formatting buttons: Bold, Italic, Underline, Strikethrough, Clear Formatting
 */
export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({ editor }) => {
  if (!editor) return null;

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor }) => {
        // Don't show when selection is empty (cursor only, no range)
        const { from, to } = editor.state.selection;
        if (from === to) return false;

        // Don't show when inside a suggestion highlight (let SuggestionBubbleMenu handle it)
        if (editor.isActive("suggestionHighlight")) return false;

        return true;
      }}
      options={{
        placement: "top",
        offset: 8,
      }}
      className="indigo-tiptap-floating-toolbar"
      pluginKey="floating-toolbar"
    >
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`indigo-tiptap-floating-toolbar__button ${
          editor.isActive("bold") ? "is-active" : ""
        }`}
        title="Bold (⌘B)"
        aria-label="Bold"
      >
        <Icon name="bold" size="s" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`indigo-tiptap-floating-toolbar__button ${
          editor.isActive("italic") ? "is-active" : ""
        }`}
        title="Italic (⌘I)"
        aria-label="Italic"
      >
        <Icon name="italic" size="s" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`indigo-tiptap-floating-toolbar__button ${
          editor.isActive("underline") ? "is-active" : ""
        }`}
        title="Underline (⌘U)"
        aria-label="Underline"
      >
        <Icon name="underline" size="s" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`indigo-tiptap-floating-toolbar__button ${
          editor.isActive("strike") ? "is-active" : ""
        }`}
        title="Strikethrough (⌘⇧S)"
        aria-label="Strikethrough"
      >
        <Icon name="strikethrough" size="s" />
      </button>
      <div className="indigo-tiptap-floating-toolbar__separator" />
      <button
        type="button"
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
        className="indigo-tiptap-floating-toolbar__button"
        title="Clear formatting"
        aria-label="Clear formatting"
      >
        <Icon name="clear-formatting" size="s" />
      </button>
    </BubbleMenu>
  );
};
